<template>
  <div
    class="d-flex flex-column"
    v-bind:style="$_containerStyle"
  >
    <div class="d-flex flex-row flex-grow-0 flex-shrink-0">
      <div
        ref="topLeft"
        class="flex-grow-0 flex-shrink-0 resize-handle"
        v-bind:style="$_topLeftStyle"
        v-on:mousedown="$_onMousedown"
      >
      </div>
      <div
        ref="top"
        class="flex-grow-1 flex-shrink-1 resize-handle"
        v-bind:style="$_topStyle"
        v-on:mousedown="$_onMousedown"
      >
      </div>
      <div
        ref="topRight"
        class="flex-grow-0 flex-shrink-0 resize-handle"
        v-bind:style="$_topRightStyle"
        v-on:mousedown="$_onMousedown"
      >
      </div>
    </div>
    <div class="d-flex flex-row flex-grow-1 overflow-hidden">
      <div
        ref="left"
        class="flex-grow-0 flex-shrink-0 resize-handle"
        v-bind:style="$_leftStyle"
        v-on:mousedown="$_onMousedown"
      >
      </div>
      <div
        class="flex-grow-1 flex-shrink-1 overflow-hidden"
        v-bind="$attrs"
        v-on="$listeners"
      >
        <slot name="default"></slot>
      </div>
      <div
        ref="right"
        class="flex-grow-0 flex-shrink-0 resize-handle"
        v-bind:style="$_rightStyle"
        v-on:mousedown="$_onMousedown"
      >
      </div>
    </div>
    <div class="d-flex flex-row flex-grow-0 flex-shrink-0">
      <div
        ref="bottomLeft"
        class="flex-grow-0 flex-shrink-0 resize-handle"
        v-bind:style="$_bottomLeftStyle"
        v-on:mousedown="$_onMousedown"
      >
      </div>
      <div
        ref="bottom"
        class="flex-grow-1 flex-shrink-1 resize-handle"
        v-bind:style="$_bottomStyle"
        v-on:mousedown="$_onMousedown"
      >
      </div>
      <div
        ref="bottomRight"
        class="flex-grow-0 flex-shrink-0 resize-handle"
        v-bind:style="$_bottomRightStyle"
        v-on:mousedown="$_onMousedown"
      >
      </div>
    </div>
  </div>
</template>

<style scoped>
div.resize-handle {
  user-select: none;
}
</style>

<script>
import Color from '../../modules/Color.js';

const resizeHandleColor = new Color(220, 220, 220);
const resizeHandleColorOnDrag = new Color(190, 190, 190);
const defaultBorderWidthPx = 3;

export default {
  watch: {
    widthPxMin() { this.$_updateWidth(this.$_widthPx) },
    widthPxMax() { this.$_updateWidth(this.$_widthPx) },
    heightPxMin() { this.$_updateHeight(this.$_heightPx) },
    heightPxMax() { this.$_updateHeight(this.$_heightPx) },
  },

  props: {
    left:          { type: Boolean, default: false },
    right:         { type: Boolean, default: false },
    top:           { type: Boolean, default: false },
    bottom:        { type: Boolean, default: false },
    borderWidthPx: { type: Number,  default: defaultBorderWidthPx },
    color:         { type: Color,   default: () => resizeHandleColor },
    colorOnDrag:   { type: Color,   default: () => resizeHandleColorOnDrag },
    widthPx:       { type: Number },
    widthPxMin:    { type: Number },
    widthPxMax:    { type: Number },
    heightPx:      { type: Number },
    heightPxMin:   { type: Number },
    heightPxMax:   { type: Number },
  },

  data() {
    return {
      $_internalWidthPx: undefined,
      $_internalHeightPx: undefined,
      $_elementAtMousedown: undefined,
      $_clientXAtMousedown: undefined,
      $_clientYAtMousedown: undefined,
      $_widthPxAtMousedown: undefined,
      $_heightPxAtMousedown: undefined,
    };
  },

  computed: {
    $_dragOnTopLeft()     { return this.$data.$_elementAtMousedown === this.$refs.topLeft },
    $_dragOnTop()         { return this.$data.$_elementAtMousedown === this.$refs.top },
    $_dragOnTopRight()    { return this.$data.$_elementAtMousedown === this.$refs.topRight },
    $_dragOnLeft()        { return this.$data.$_elementAtMousedown === this.$refs.left },
    $_dragOnRight()       { return this.$data.$_elementAtMousedown === this.$refs.right },
    $_dragOnBottomLeft()  { return this.$data.$_elementAtMousedown === this.$refs.bottomLeft },
    $_dragOnBottom()      { return this.$data.$_elementAtMousedown === this.$refs.bottom },
    $_dragOnBottomRight() { return this.$data.$_elementAtMousedown === this.$refs.bottomRight },

    $_widthPx()  { return (this.widthPx !== undefined)? this.widthPx : this.$data.$_internalWidthPx },
    $_heightPx() { return (this.heightPx !== undefined)? this.heightPx : this.$data.$_internalHeightPx },

    $_containerStyle() {
      let containerStyle = {};
      if (this.$_widthPx !== undefined) {
        let widthValueString = `${this.$_widthPx}px`;
        containerStyle.minWidth = widthValueString;
        containerStyle.maxWidth = widthValueString;
      } else {
        containerStyle.width = '100%';
      }
      if (this.$_heightPx !== undefined) {
        let heightValueString = `${this.$_heightPx}px`;
        containerStyle.minHeight = heightValueString;
        containerStyle.maxHeight = heightValueString;
      } else {
        containerStyle.height = '100%';
      }
      return containerStyle;
    },

    $_topLeftStyle() {
      if (this.top && this.left) {
        return {
          width: `${this.borderWidthPx}px`,
          height: `${this.borderWidthPx}px`,
          backgroundColor: ((this.$_dragOnTopLeft)? this.colorOnDrag : this.color).styleString,
          cursor: 'nwse-resize',
        };
      }
      return { display: 'none' };
    },

    $_topStyle() {
      if (this.top) {
        return {
          height: `${this.borderWidthPx}px`,
          backgroundColor: ((this.$_dragOnTop)? this.colorOnDrag : this.color).styleString,
          cursor: 'ns-resize',
        };
      }
      return { display: 'none' };
    },

    $_topRightStyle() {
      if (this.top && this.right) {
        return {
          width: `${this.borderWidthPx}px`,
          height: `${this.borderWidthPx}px`,
          backgroundColor: ((this.$_dragOnTopRight)? this.colorOnDrag : this.color).styleString,
          cursor: 'nesw-resize',
        };
      }
      return { display: 'none' };
    },

    $_leftStyle() {
      if (this.left) {
        return {
          width: `${this.borderWidthPx}px`,
          backgroundColor: ((this.$_dragOnLeft)? this.colorOnDrag : this.color).styleString,
          cursor: 'ew-resize',
        };
      }
      return { display: 'none' };
    },

    $_rightStyle() {
      if (this.right) {
        return {
          width: `${this.borderWidthPx}px`,
          backgroundColor: ((this.$_dragOnRight)? this.colorOnDrag : this.color).styleString,
          cursor: 'ew-resize',
        };
      }
      return { display: 'none' };
    },

    $_bottomLeftStyle() {
      if (this.bottom && this.left) {
        return {
          width: `${this.borderWidthPx}px`,
          height: `${this.borderWidthPx}px`,
          backgroundColor: ((this.$_dragOnBottomLeft)? this.colorOnDrag : this.color).styleString,
          cursor: 'nesw-resize',
        };
      }
      return { display: 'none' };
    },

    $_bottomStyle() {
      if (this.bottom) {
        return {
          height: `${this.borderWidthPx}px`,
          backgroundColor: ((this.$_dragOnBottom)? this.colorOnDrag : this.color).styleString,
          cursor: 'ns-resize',
        };
      }
      return { display: 'none' };
    },

    $_bottomRightStyle() {
      if (this.bottom && this.right) {
        return {
          width: `${this.borderWidthPx}px`,
          height: `${this.borderWidthPx}px`,
          backgroundColor: ((this.$_dragOnBottomRight)? this.colorOnDrag : this.color).styleString,
          cursor: 'nwse-resize',
        };
      }
      return { display: 'none' };
    },
  },

  methods: {
    /* public */
    onMousemove(mouseEvent) {
      if (mouseEvent.buttons === 0) {
        this.onMouseup();
        return;
      }
      let clientXAtMousemove = mouseEvent.clientX;
      let clientYAtMousemove = mouseEvent.clientY;
      switch (this.$data.$_elementAtMousedown) {
        case this.$refs.topLeft:
          dragTopHandle.call(this, clientYAtMousemove);
          dragLeftHandle.call(this, clientXAtMousemove);
          break;
        case this.$refs.top:
          dragTopHandle.call(this, clientYAtMousemove);
          break;
        case this.$refs.topRight:
          dragTopHandle.call(this, clientYAtMousemove);
          dragRightHandle.call(this, clientXAtMousemove);
          break;
        case this.$refs.left:
          dragLeftHandle.call(this, clientXAtMousemove);
          break;
        case this.$refs.right:
          dragRightHandle.call(this, clientXAtMousemove);
          break;
        case this.$refs.bottomLeft:
          dragBottomHandle.call(this, clientYAtMousemove);
          dragLeftHandle.call(this, clientXAtMousemove);
          break;
        case this.$refs.bottom:
          dragBottomHandle.call(this, clientYAtMousemove);
          break;
        case this.$refs.bottomRight:
          dragBottomHandle.call(this, clientYAtMousemove);
          dragRightHandle.call(this, clientXAtMousemove);
          break;
        default:
          break;
      }

      function dragTopHandle(clientYAtMousemove) {
        let dragOffsetY = this.$data.$_clientYAtMousedown - clientYAtMousemove;
        this.$_updateHeight(this.$data.$_heightPxAtMousedown + dragOffsetY);
      }

      function dragBottomHandle(clientYAtMousemove) {
        let dragOffsetY = clientYAtMousemove - this.$data.$_clientYAtMousedown;
        this.$_updateHeight(this.$data.$_heightPxAtMousedown + dragOffsetY);
      }

      function dragLeftHandle(clientXAtMousemove) {
        let dragOffsetX = this.$data.$_clientXAtMousedown - clientXAtMousemove;
        this.$_updateWidth(this.$data.$_widthPxAtMousedown + dragOffsetX)
      }

      function dragRightHandle(clientXAtMousemove) {
        let dragOffsetX = clientXAtMousemove - this.$data.$_clientXAtMousedown;
        this.$_updateWidth(this.$data.$_widthPxAtMousedown + dragOffsetX)
      }
    },

    onMouseup() {
      this.$data.$_elementAtMousedown = undefined;
      this.$data.$_clientXAtMousedown = undefined;
      this.$data.$_clientYAtMousedown = undefined;
      this.$data.$_widthPxAtMousedown = undefined;
      this.$data.$_heightPxAtMousedown = undefined;
    },

    /* private */
    $_onMousedown(mouseEvent) {
      let boundingClientRect = this.$el.getBoundingClientRect();
      this.$data.$_elementAtMousedown = mouseEvent.srcElement;
      this.$data.$_clientXAtMousedown = mouseEvent.clientX;
      this.$data.$_clientYAtMousedown = mouseEvent.clientY;
      this.$data.$_widthPxAtMousedown = boundingClientRect.width;
      this.$data.$_heightPxAtMousedown = boundingClientRect.height;
    },

    $_updateWidth(widthPx) {
      if ((this.widthPxMin !== undefined) && (widthPx < this.widthPxMin)) widthPx = this.widthPxMin;
      if ((this.widthPxMax !== undefined) && (widthPx > this.widthPxMax)) widthPx = this.widthPxMax;
      if (this.widthPx !== undefined) {
        this.$emit('update:width-px', widthPx);
      } else {
        this.$data.$_internalWidthPx = widthPx;
      }
    },

    $_updateHeight(heightPx) {
      if ((this.heightPxMin !== undefined) && (heightPx < this.heightPxMin)) heightPx = this.heightPxMin;
      if ((this.heightPxMax !== undefined) && (heightPx > this.heightPxMax)) heightPx = this.heightPxMax;
      if (this.heightPx !== undefined) {
        this.$emit('update:height-px', heightPx);
      } else {
        this.$data.$_internalHeightPx = heightPx;
      }
    },
  },
}
</script>
